import { useState, useRef, useEffect, useCallback } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import { useListTendersQuery } from "@/state/tendersApi";
import { useSubmitTenderSupportMutation } from "@/state/submissionsApi";
import type { Tender } from "@/types/tenders";

const supportOptions = [
  "supportTypeRegistration",
  "supportTypeIdentification",
  "supportTypeBidWriting",
  "supportTypeCompliance",
  "supportTypeFinancial",
  "supportTypeBidBond",
  "supportTypePerformanceBond",
  "supportTypeFull",
  "supportTypeOther",
];

const TenderSupportSchema = z.object({
  companyName: z.string().min(1, "Company name is required"),
  contactName: z.string().min(1, "Contact name is required"),
  email: z.email("Invalid email address"),
  phone: z.string().optional(),
  country: z.string().optional(),
  tenderReference: z.string().optional(),
  supportType: z.string().optional(),
  additionalInfo: z.string().max(500).optional(),
});

type TenderSupportFormValues = z.infer<typeof TenderSupportSchema>;

function TenderAutocomplete({
  tenders,
  loading,
  value,
  onChange,
  t,
}: {
  tenders: Tender[];
  loading: boolean;
  value: string;
  onChange: (ref: string) => void;
  t: (key: string) => string;
}) {
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [selectedTender, setSelectedTender] = useState<Tender | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const filtered = tenders.filter(
    (tender) =>
      tender.title.toLowerCase().includes(query.toLowerCase()) ||
      tender.reference.toLowerCase().includes(query.toLowerCase())
  );

  const handleSelect = useCallback(
    (tender: Tender) => {
      setSelectedTender(tender);
      setQuery(`${tender.title} (${tender.reference})`);
      onChange(tender.reference);
      setIsOpen(false);
      setHighlightedIndex(-1);
    },
    [onChange]
  );

  const handleClear = useCallback(() => {
    setSelectedTender(null);
    setQuery("");
    onChange("");
    setHighlightedIndex(-1);
    setIsOpen(false);
    inputRef.current?.focus();
  }, [onChange]);

  // Sync internal state when value changes externally (prefilled from modal, form reset)
  useEffect(() => {
    if (!value && selectedTender) {
      // Form was reset / cleared
      setSelectedTender(null);
      setQuery("");
    } else if (value && (!selectedTender || selectedTender.reference !== value)) {
      // Value was prefilled from outside (e.g. modal click) — find the matching tender
      const match = tenders.find((t) => t.reference === value);
      if (match) {
        setSelectedTender(match);
        setQuery(`${match.title} (${match.reference})`);
      } else {
        // No match yet — tenders may still be loading, show the raw reference
        setSelectedTender(null);
        setQuery(value);
      }
    }
  }, [value, selectedTender, tenders]);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < filtered.length - 1 ? prev + 1 : 0
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev > 0 ? prev - 1 : filtered.length - 1
        );
        break;
      case "Enter":
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filtered.length) {
          handleSelect(filtered[highlightedIndex]);
        }
        break;
      case "Escape":
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;
    }
  };

  return (
    <div className="flex flex-col gap-1.5 relative" ref={wrapperRef}>
      <label className="text-xs font-semibold text-[#0A1628] uppercase tracking-wide">
        {t("supportFormRef")}
      </label>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          placeholder={t("searchTenderPlaceholder")}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setIsOpen(true);
            setSelectedTender(null);
            onChange("");
            setHighlightedIndex(-1);
          }}
          onFocus={() => {
            if (!selectedTender) setIsOpen(true);
          }}
          onKeyDown={handleKeyDown}
          className="text-sm border border-gray-200 rounded-lg px-4 py-3 bg-[#f8fafc] focus:outline-none focus:border-[#00C2C2] text-[#0A1628] placeholder-gray-400 transition-colors w-full"
        />
        {selectedTender && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
            tabIndex={-1}
          >
            <i className="ri-close-line" />
          </button>
        )}
      </div>

      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto">
          {loading ? (
            <div className="px-4 py-3 text-sm text-gray-400 flex items-center gap-2">
              <i className="ri-loader-4-line animate-spin" /> Loading...
            </div>
          ) : filtered.length === 0 ? (
            <div className="px-4 py-3 text-sm text-gray-400">
              No matching tenders found
            </div>
          ) : (
            filtered.map((tender, index) => (
              <button
                key={tender.id}
                type="button"
                onClick={() => handleSelect(tender)}
                onMouseEnter={() => setHighlightedIndex(index)}
                className={`w-full text-left px-4 py-3 text-sm transition-colors cursor-pointer ${
                  index === highlightedIndex
                    ? "bg-[#00C2C2]/10 text-[#0A1628]"
                    : "text-[#0A1628] hover:bg-[#f8fafc]"
                }`}
              >
                <p className="font-medium leading-snug">{tender.title}</p>
                <p className="text-xs text-gray-400 mt-0.5">
                  {tender.reference}
                  {tender.client && (
                    <>
                      <span className="mx-1.5">·</span>
                      {tender.client}
                    </>
                  )}
                </p>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

type Props = {
  prefilledReference?: string;
}

export default function TenderSupportForm({ prefilledReference = "" }: Props) {
  const { t } = useTranslation();
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [pendingType, setPendingType] = useState<"support" | "interest">("support");
  const [submitTenderSupport, { isLoading }] = useSubmitTenderSupportMutation();

  const { data: tendersData, isLoading: tendersLoading } = useListTendersQuery({});
  const tenders = tendersData?.data ?? [];
  const hasTenders = !tendersLoading && tenders.length > 0;

  const { register, handleSubmit, formState: { errors }, watch, reset, setValue } = useForm<TenderSupportFormValues>({
    resolver: zodResolver(TenderSupportSchema),
    defaultValues: {
      companyName: "", contactName: "", email: "", phone: "",
      country: "", tenderReference: prefilledReference, supportType: "", additionalInfo: "",
    },
  });

  // Sync prefilled reference when it changes (e.g. from modal click)
  useEffect(() => {
    if (prefilledReference) {
      setValue("tenderReference", prefilledReference, { shouldValidate: true });
    }
  }, [prefilledReference, setValue]);

  const additionalInfoLength = watch("additionalInfo")?.length ?? 0;

  const onSubmit = async (data: TenderSupportFormValues) => {
    setSubmitError("");
    try {
      const tender = await submitTenderSupport({
        submissionType: pendingType,
        companyName: data.companyName,
        contactName: data.contactName,
        email: data.email,
        phone: data.phone || undefined,
        country: data.country || undefined,
        tenderReference: data.tenderReference || undefined,
        supportType: data.supportType || undefined,
        additionalInfo: data.additionalInfo || undefined,
      }).unwrap();
      if (tender.success) {
        setSubmitted(true);
        reset();
      }
    } catch (err) {
      const message = (err as any)?.data?.error?.message;
      setSubmitError(message ?? "Something went wrong. Please try again.");
    }
  };

  return (
    <section id="tender-support" className="py-24 bg-[#f8fafc]">
      <div className="w-full px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          <div>
            <p className="text-[#00C2C2] text-xs font-semibold tracking-widest uppercase mb-4">{t("supportTag")}</p>
            <h2 className="text-4xl lg:text-5xl font-bold text-[#0A1628] leading-tight mb-6">
              {t("supportTitle1")} <span className="text-[#00C2C2]">{t("supportTitle2")}</span>
            </h2>
            <p className="text-gray-500 text-base leading-relaxed mb-10">
              {t("supportDesc")}
            </p>

            <div className="flex flex-col gap-6">
              {[
                { icon: "ri-community-line", title: t("supportService1"), desc: t("supportService1Desc") },
                { icon: "ri-file-search-line", title: t("supportService2"), desc: t("supportService2Desc") },
                { icon: "ri-pencil-ruler-2-line", title: t("supportService3"), desc: t("supportService3Desc") },
                { icon: "ri-shield-check-line", title: t("supportService4"), desc: t("supportService4Desc") },
                { icon: "ri-customer-service-2-line", title: t("supportService5"), desc: t("supportService5Desc") },
                { icon: "ri-safe-2-line", title: t("supportService6"), desc: t("supportService6Desc") },
                { icon: "ri-award-line", title: t("supportService7"), desc: t("supportService7Desc") },
              ].map((item) => (
                <div key={item.title} className="flex gap-4 items-start">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#0A1628]/10 text-[#00C2C2] shrink-0">
                    <i className={`${item.icon} text-base`} />
                  </div>
                  <div>
                    <p className="font-bold text-[#0A1628] text-sm mb-1">{item.title}</p>
                    <p className="text-gray-500 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-8">
            <h3 className="text-lg font-bold text-[#0A1628] mb-1">{t("supportFormTitle")}</h3>
            <p className="text-xs text-gray-500 mb-6">{t("supportFormSubtitle")}</p>

            {submitted ? (
              <div className="text-center py-10">
                <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#0A1628] text-white text-2xl mx-auto mb-4">
                  <i className="ri-check-line" />
                </div>
                <h4 className="text-lg font-bold text-[#0A1628] mb-2">{t("supportFormSuccessTitle")}</h4>
                <p className="text-gray-500 text-sm">{t("supportFormSuccessBody")}</p>
                <button onClick={() => setSubmitted(false)} className="mt-6 text-[#00C2C2] text-sm font-semibold hover:underline cursor-pointer whitespace-nowrap">
                  {t("submitAnother")}
                </button>
              </div>
            ) : (
              <form id="tender-support-form" onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
                {submitError && (
                  <p className="text-xs text-red-500 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{submitError}</p>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-semibold text-[#0A1628] uppercase tracking-wide">{t("supportFormCompany")} *</label>
                    <input type="text" placeholder={t("company")} {...register("companyName")} className="text-sm border border-gray-200 rounded-lg px-4 py-3 bg-[#f8fafc] focus:outline-none focus:border-[#00C2C2] text-[#0A1628] placeholder-gray-400 transition-colors" />
                    {errors.companyName && <p className="text-xs text-red-500">{errors.companyName.message}</p>}
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-semibold text-[#0A1628] uppercase tracking-wide">{t("supportFormContact")} *</label>
                    <input type="text" placeholder={t("fullName")} {...register("contactName")} className="text-sm border border-gray-200 rounded-lg px-4 py-3 bg-[#f8fafc] focus:outline-none focus:border-[#00C2C2] text-[#0A1628] placeholder-gray-400 transition-colors" />
                    {errors.contactName && <p className="text-xs text-red-500">{errors.contactName.message}</p>}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-semibold text-[#0A1628] uppercase tracking-wide">{t("supportFormEmail")} *</label>
                    <input type="email" placeholder="email@company.com" {...register("email")} className="text-sm border border-gray-200 rounded-lg px-4 py-3 bg-[#f8fafc] focus:outline-none focus:border-[#00C2C2] text-[#0A1628] placeholder-gray-400 transition-colors" />
                    {errors.email && <p className="text-xs text-red-500">{errors.email.message}</p>}
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-semibold text-[#0A1628] uppercase tracking-wide">{t("supportFormPhone")}</label>
                    <input type="tel" placeholder="+250 7XX XXX XXX" {...register("phone")} className="text-sm border border-gray-200 rounded-lg px-4 py-3 bg-[#f8fafc] focus:outline-none focus:border-[#00C2C2] text-[#0A1628] placeholder-gray-400 transition-colors" />
                  </div>
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-semibold text-[#0A1628] uppercase tracking-wide">{t("supportFormCountry")}</label>
                  <select {...register("country")} className="text-sm border border-gray-200 rounded-lg px-4 py-3 bg-[#f8fafc] focus:outline-none focus:border-[#00C2C2] text-[#0A1628] transition-colors cursor-pointer">
                    <option value="">{t("supportFormSelectCountry")}</option>
                    <option value="Rwanda">Rwanda</option>
                    <option value="Kenya">Kenya</option>
                    <option value="Tanzania">Tanzania</option>
                    <option value="Uganda">Uganda</option>
                    <option value="Ethiopia">Ethiopia</option>
                    <option value="Burundi">Burundi</option>
                    <option value="DRC">DRC</option>
                    <option value="Other">{t("supportTypeOther")}</option>
                  </select>
                </div>

                {hasTenders && <TenderAutocomplete
                  tenders={tenders}
                  loading={tendersLoading}
                  value={watch("tenderReference") ?? ""}
                  onChange={(ref) => setValue("tenderReference", ref, { shouldValidate: true })}
                  t={t}
                />}

                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-semibold text-[#0A1628] uppercase tracking-wide">{t("supportFormType")}</label>
                  <select {...register("supportType")} className="text-sm border border-gray-200 rounded-lg px-4 py-3 bg-[#f8fafc] focus:outline-none focus:border-[#00C2C2] text-[#0A1628] transition-colors cursor-pointer">
                    <option value="">{t("supportFormSelectSupport")}</option>
                    {supportOptions.map((opt) => (
                      <option key={opt} value={t(opt)}>{t(opt)}</option>
                    ))}
                  </select>
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-semibold text-[#0A1628] uppercase tracking-wide">{t("supportFormInfo")}</label>
                  <textarea rows={3} maxLength={500} placeholder="Any additional context about your requirement..." {...register("additionalInfo")} className="text-sm border border-gray-200 rounded-lg px-4 py-3 bg-[#f8fafc] focus:outline-none focus:border-[#00C2C2] text-[#0A1628] placeholder-gray-400 resize-none transition-colors" />
                  <p className="text-xs text-gray-400 text-right">{additionalInfoLength}/500</p>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 pt-1">
                  <button
                    type="submit"
                    disabled={isLoading}
                    onClick={() => setPendingType("support")}
                    className="flex-1 bg-[#0A1628] text-white text-sm font-bold py-3 rounded-lg hover:bg-[#0A1628] transition-colors whitespace-nowrap disabled:opacity-60 cursor-pointer flex items-center justify-center gap-2"
                  >
                    {isLoading && pendingType === "support" ? <><i className="ri-loader-4-line animate-spin" /> {t("processing")}</> : <><i className="ri-send-plane-line" /> {t("supportFormSubmit")}</>}
                  </button>
                  {hasTenders && (
                    <button
                      type="submit"
                      disabled={isLoading}
                      onClick={() => setPendingType("interest")}
                      className="flex-1 bg-[#0A1628] text-white text-sm font-bold py-3 rounded-lg hover:bg-[#0A1628] transition-colors whitespace-nowrap disabled:opacity-60 cursor-pointer flex items-center justify-center gap-2"
                    >
                      {isLoading && pendingType === "interest" ? <><i className="ri-loader-4-line animate-spin" /> {t("processing")}</> : <><i className="ri-file-add-line" /> {t("supportFormSubmitInterest")}</>}
                    </button>
                  )}
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
